"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSettingTable = useSettingTable;
const DataTable_1 = require("@juvare/common-data-table-ui/DataTable");
const FiltersBar_1 = require("@juvare/common-data-table-ui/FiltersBar");
const JuvareTheme_1 = require("@juvare/common-web-ui/JuvareTheme");
const hooks_1 = require("../../../../ui/hooks");
const usePlatformSdkReactTranslation_1 = require("../../../../ui/hooks/usePlatformSdkReactTranslation");
const react_1 = __importDefault(require("react"));
const mapSettingFilterData_1 = require("../utils/mapSettingFilterData");
function useSettingTable({ useDataTableFunction = DataTable_1.useDataTableUsingUrl, onRowClick }) {
    const { t } = (0, usePlatformSdkReactTranslation_1.usePlatformSdkReactTranslation)();
    const filterConfig = (0, FiltersBar_1.useFilterConfig)((builder) => {
        builder.quickSearchFilter({
            dataPath: 'search',
            label: t('common.search'),
        });
        builder.quickFilterSelect({
            dataPath: 'changed',
            label: t('editableSettings.changed'),
            values: [
                { label: t('common.all') },
                { value: true, label: t('editableSettings.onlyChanged') },
                { value: false, label: t('editableSettings.onlyDefault') },
            ],
            widget: 'form.radio',
        });
        builder.quickFilterSelect({
            dataPath: 'public',
            label: t('editableSettings.visibility'),
            values: [
                { label: t('common.all') },
                { value: true, label: t('editableSettings.onlyPublic') },
                { value: false, label: t('editableSettings.onlyPrivate') },
            ],
            widget: 'form.radio',
        });
    }, []);
    const columns = react_1.default.useMemo(() => [
        {
            headerLabel: t('editableSettings.setting'),
            sortKey: 'description',
            cellFunction: (row) => (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(DataTable_1.TextCell, null, row.columns.description),
                react_1.default.createElement(DataTable_1.TextCell, { sx: { display: { xl: 'none' }, color: JuvareTheme_1.themeColors.text.subtle } }, row.columns.name))),
            tableCellSx: {
                width: {
                    xs: '60%',
                    xl: '40%',
                },
            },
        },
        {
            headerLabel: t('common.name'),
            sortKey: 'name',
            cellFunction: (row) => react_1.default.createElement(DataTable_1.TextCell, null, row.columns.name),
            tableCellSx: {
                display: {
                    xs: 'none',
                    xl: 'table-cell',
                },
                width: {
                    xl: '20%',
                },
            },
        },
        {
            headerLabel: t('common.value'),
            cellFunction: (row) => (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(DataTable_1.TextCell, null, row.columns.type === 'json' ? (react_1.default.createElement("em", null, t('editableSettings.valueJson'))) : row.columns.type === 'image' ? (react_1.default.createElement("em", null, t('editableSettings.valueImage'))) : (row.columns.value)),
                react_1.default.createElement(DataTable_1.TextCell, { sx: { display: { md: 'none' }, color: JuvareTheme_1.themeColors.text.subtle } }, row.columns.changed ? t('editableSettings.changed') : t('editableSettings.default')))),
            tableCellSx: {
                width: {
                    xs: '40%',
                },
            },
        },
        {
            headerLabel: t('editableSettings.changed'),
            sortKey: 'changed',
            cellFunction: (row) => (react_1.default.createElement(DataTable_1.TextCell, null, row.columns.changed ? t('editableSettings.changed') : t('editableSettings.default'))),
            tableCellSx: {
                display: {
                    xs: 'none',
                    md: 'table-cell',
                },
                width: 120,
            },
        },
        {
            headerLabel: t('editableSettings.visibility'),
            sortKey: 'public',
            cellFunction: (row) => (react_1.default.createElement(DataTable_1.TextCell, null, row.columns.public ? t('editableSettings.public') : t('editableSettings.private'))),
            tableCellSx: {
                display: {
                    xs: 'none',
                    lg: 'table-cell',
                },
                width: 120,
            },
        },
    ], []);
    const params = {
        useDataProvider: (request) => {
            const response = (0, hooks_1.usePlatformFunction)('getSettingsByGroup', {
                filter: (0, mapSettingFilterData_1.mapSettingFilterData)(request.filterData),
                orderBy: [{ column: request.sortColumnKey, desc: request.sortColumnDesc }],
                limit: request.limit,
                offset: request.offset,
            });
            return {
                data: response.data
                    ? {
                        ...response.data.result,
                        filteredRowCount: response.data.result.total,
                    }
                    : undefined,
                error: response.error,
                isLoading: response.isLoading,
                reload: response.mutate,
            };
        },
        defaultSortColumnKey: 'description',
        onRowClick: onRowClick,
    };
    const { dataTableProps, filtersBarProps, appliedFilterData, clearActiveRow, reload, setSelectedRowIds, setActiveRowId } = useDataTableFunction(params);
    return {
        dataTableProps: {
            ...dataTableProps,
            rows: dataTableProps.rows.map((row) => ({ ...row, id: row.columns.name })),
            title: t('editableSettings.settings'),
            columns: columns,
            filtersBar: react_1.default.createElement(FiltersBar_1.FiltersBar, { ...filtersBarProps, config: filterConfig }),
            tableSx: { tableLayout: 'fixed' },
        },
        filtersBarProps: filtersBarProps,
        appliedFilterData: appliedFilterData,
        clearActiveRow: clearActiveRow,
        setSelectedRowIds: setSelectedRowIds,
        setActiveRowId: setActiveRowId,
        reload: reload,
    };
}
